import { milliseconds } from "date-fns/milliseconds";
import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../store/hooks";
import type { FormResponseCategory, HideUnsubmittedAfter, HideUnsubmittedFormRule } from "../../shared/schema";

export interface UseEnduserFormResponsesOptions {
  limit?: number;
  /** Filter by form response category */
  category?: FormResponseCategory;
  /** Form IDs to hide (typically from organization settings) */
  hiddenFormIds?: string[];
  /** Whether to enable the query (defaults to true) */
  enabled?: boolean;
  /** Response IDs to exclude — used to hide pinned-form outstanding responses
   *  that the pinned forms block will reuse rather than create duplicates */
  excludeResponseIds?: string[];
  /** Hide unsubmitted responses once this much time has passed since they were sent (pushed to the portal, else created) */
  hideUnsubmittedAfter?: HideUnsubmittedAfter;
  /** Per-form overrides of `hideUnsubmittedAfter`; the first rule listing a response's form wins */
  hideUnsubmittedFormRules?: HideUnsubmittedFormRule[];
}

/**
 * Hook for fetching and filtering form responses for the current enduser.
 *
 * Filtering logic:
 * - Not manually hidden from enduser portal
 * - Not an unsubmitted internal note
 * - One of:
 *   - Submitted by this enduser, OR
 *   - Outstanding (needs completion - no submittedBy and no draftSavedAt), OR
 *   - Has fields shared with enduser
 * - Form not in organization's hidden list
 *
 * Category filtering:
 * - "submitted": Only form responses with submittedAt
 * - "unsubmitted": Only form responses without submittedAt
 * - "all": All visible form responses
 *
 * Unsubmitted responses sent (pushed to the portal, else created) longer ago than their form's hide window
 * are dropped. The window comes from the first `hideUnsubmittedFormRules` entry listing the form, falling
 * back to `hideUnsubmittedAfter`.
 */
export function useEnduserFormResponses(options: UseEnduserFormResponsesOptions = {}) {
  const {
    limit = 50,
    category = "all",
    hiddenFormIds = [],
    enabled = true,
    excludeResponseIds = [],
    hideUnsubmittedAfter,
    hideUnsubmittedFormRules = [],
  } = options;
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);

  const query = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: {},
      limit,
    },
    select: (data) => data.pages.flat(),
    enabled,
  });

  const allResponses = query.data ?? [];

  // Get current enduser ID for filtering
  const enduserId = currentEnduser?.id;

  // Apply visibility filtering logic
  const visibleResponses = allResponses.filter((f) => {
    // Hidden because the pinned forms block will reuse this response
    if (excludeResponseIds.includes(f.id)) return false;

    // Not manually hidden from enduser portal
    if (f.hideFromEnduserPortal) return false;

    // Not an unsubmitted internal note
    if (!f.submittedAt && !!f.isInternalNote) return false;

    // Form not in organization's hidden list
    if (hiddenFormIds.includes(f.formId)) return false;

    // Must meet one of these visibility criteria:
    const submittedByEnduser = f.submittedBy === enduserId;
    const isOutstanding = !f.submittedBy; // includes both pure-pending and draft-saved states
    const hasSharedFields = !!f.responses?.find((r) => r.sharedWithEnduser === true);

    return submittedByEnduser || isOutstanding || hasSharedFields;
  });

  // Apply category filtering
  const categoryResponses = visibleResponses.filter((f) => {
    switch (category) {
      case "submitted":
        return !!f.submittedAt;
      case "unsubmitted":
        return !f.submittedAt;
      case "all":
      default:
        return true;
    }
  });

  const now = Date.now();
  const filteredResponses = categoryResponses.filter((f) => {
    if (f.submittedAt) return true;
    const rule = hideUnsubmittedFormRules.find((r) => r.formIds.includes(f.formId));
    const hideAfter = rule ? rule.hideAfter : hideUnsubmittedAfter;
    if (!hideAfter) return true;
    // A response pushed to the portal after it was created reached the patient at the push, not at creation
    const sentAt = new Date(f.pushedToPortalAt ?? f.createdAt).getTime();
    return sentAt >= now - milliseconds({ [hideAfter.unit]: hideAfter.amount });
  });

  // Sort by most recent activity (submittedAt, draftSavedAt, or updatedAt)
  const sortedResponses = [...filteredResponses].sort((a, b) => {
    const aTime = a.submittedAt
      ? new Date(a.submittedAt).getTime()
      : a.draftSavedAt
      ? new Date(a.draftSavedAt).getTime()
      : new Date(a.updatedAt).getTime();
    const bTime = b.submittedAt
      ? new Date(b.submittedAt).getTime()
      : b.draftSavedAt
      ? new Date(b.draftSavedAt).getTime()
      : new Date(b.updatedAt).getTime();
    return bTime - aTime;
  });

  return {
    ...query,
    formResponses: sortedResponses,
  };
}
